import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useWindowWidth } from '../hooks/useDebounce';
import { PDFDocument as PDFLibDocument, rgb, StandardFonts } from 'pdf-lib';
import { Document, Page, pdfjs } from 'react-pdf';
import '@react-pdf/renderer';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import "react-pdf/dist/esm/Page/TextLayer.css";
import { PDFViewer, PDFDownloadLink, Page as PdfPage, Text, View, Document as PdfDocument, StyleSheet } from '@react-pdf/renderer';
import { styled } from '@mui/material/styles';
import Button, { ButtonProps } from '@mui/material/Button';
import fontkit from '@pdf-lib/fontkit';
import '../index.css';
import { FaEdit } from 'react-icons/fa';
import Slider from '@mui/material/Slider';
import { Typography, TextField, Autocomplete, Checkbox, FormControlLabel, Box, Select, MenuItem } from '@mui/material';
import SignatureModal from "./SignatureModal.jsx";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import axios from "axios";
import SimpleSnackbar from './SimpleSnackbar.jsx';
import { InlineSpinner } from './loading';
import Draggable from 'react-draggable';
import AddIcon from '@mui/icons-material/Add';
import { colors, spacing, typography, shadows, radius, transitions } from '../theme/tokens';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

// ============================================================================
// STYLED COMPONENTS (Design Token Migration - AIF-24)
// ============================================================================

const PageHeader = styled('div')({
  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
  color: colors.neutral.white,
  padding: `${spacing.xl} 0`,
  marginBottom: spacing.xl,
  boxShadow: shadows.card,
});

const HeaderTitle = styled('h1')({
  fontSize: typography.fontSize['4xl'],
  fontWeight: typography.fontWeight.bold,
  margin: 0,
  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
});

const HeaderSubtitle = styled('p')({
  fontSize: typography.fontSize.lg,
  margin: `${spacing.sm} 0 0 0`,
  opacity: 0.9,
});

const FileInfoCard = styled('div')({
  background: 'rgba(255,255,255,0.15)',
  padding: `${spacing.md} ${spacing.lg}`,
  borderRadius: radius.md,
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.2)',
});

const FileInfoLabel = styled('small')({
  opacity: 0.9,
  color: colors.neutral[50],
});

const FileInfoText = styled('div')({
  fontWeight: typography.fontWeight.semibold,
});

const UploadSection = styled('div')({
  background: colors.background.paper,
  border: `2px dashed ${colors.border.light}`,
  borderRadius: radius.card,
  padding: spacing.xl,
  textAlign: 'center',
  transition: transitions.all,
  boxShadow: shadows.sm,
});

const UploadIcon = styled('div')({
  fontSize: typography.fontSize['6xl'],
  color: colors.primary.main,
  marginBottom: spacing.lg,
});

const UploadLabel = styled('label')({
  fontSize: typography.fontSize.xl,
  fontWeight: typography.fontWeight.semibold,
  color: colors.text.primary,
  cursor: 'pointer',
  marginBottom: spacing.sm,
  display: 'block',
});

const UploadHelpText = styled('p')({
  color: colors.text.secondary,
  margin: 0,
  fontSize: typography.fontSize.sm,
});

const FloatingControlPanel = styled('div')({
  position: 'fixed',
  bottom: '30px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '15px',
});

const QuickAddPanel = styled('div')({
  background: `linear-gradient(145deg, ${colors.background.paper} 0%, ${colors.background.subtle} 100%)`,
  border: `1px solid ${colors.border.light}`,
  borderRadius: radius.xl,
  padding: spacing.lg,
  boxShadow: `${shadows.xl}, 0 8px 24px rgba(19, 36, 161, 0.15)`,
  minWidth: '350px',
  backdropFilter: 'blur(10px)',
});

const PanelHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginBottom: spacing.lg,
  borderBottom: `2px solid ${colors.border.light}`,
  paddingBottom: spacing.md,
});

const PanelIcon = styled('div')({
  fontSize: typography.fontSize['2xl'],
  marginRight: spacing.md,
});

const PanelTitle = styled('h5')({
  margin: 0,
  textAlign: 'right',
  fontSize: typography.fontSize.xl,
  fontWeight: typography.fontWeight.semibold,
  color: colors.text.primary,
});

const QuickAddGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: spacing.md,
});

const QuickAddButton = styled('button')({
  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
  color: colors.neutral.white,
  border: 'none',
  borderRadius: radius.md,
  padding: `${spacing.md} ${spacing.lg}`,
  textAlign: 'right',
  fontSize: typography.fontSize.base,
  fontWeight: typography.fontWeight.medium,
  cursor: 'pointer',
  transition: transitions.all,
  boxShadow: `0 4px 8px rgba(19, 36, 161, 0.3)`,
  '&:hover': {
    transform: 'translateY(-2px)',
    // Use filter instead of boxShadow for better performance (GPU-accelerated)
    filter: 'drop-shadow(0 6px 12px rgba(19, 36, 161, 0.4))',
  },
});

const ViewAllFieldsButton = styled('button')({
  background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
  color: colors.neutral.white,
  border: 'none',
  borderRadius: radius.md,
  padding: `${spacing.md} ${spacing.lg}`,
  textAlign: 'right',
  fontSize: typography.fontSize.base,
  fontWeight: typography.fontWeight.semibold,
  cursor: 'pointer',
  transition: transitions.all,
  boxShadow: '0 4px 8px rgba(255, 152, 0, 0.3)',
  '&:hover': {
    transform: 'translateY(-2px)',
    // Use filter instead of boxShadow for better performance (GPU-accelerated)
    filter: 'drop-shadow(0 6px 12px rgba(255, 152, 0, 0.4))',
  },
});

const MainActionButton = styled('button')({
  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
  color: colors.neutral.white,
  border: 'none',
  borderRadius: radius.full,
  padding: `${spacing.lg} ${spacing['2xl']}`,
  fontSize: typography.fontSize.lg,
  fontWeight: typography.fontWeight.semibold,
  cursor: 'pointer',
  boxShadow: shadows.lg,
  transition: transitions.all,
  display: 'flex',
  alignItems: 'center',
  gap: spacing.md,
  '&:hover': {
    transform: 'translateY(-2px)',
    // Use filter instead of boxShadow for better performance (GPU-accelerated)
    filter: 'drop-shadow(0 12px 24px rgba(19, 36, 161, 0.4))',
  },
});

const ActionButtonIcon = styled('span')({
  fontSize: typography.fontSize.xl,
});

const CategorizedFieldsPanel = styled('div')({
  background: colors.background.paper,
  border: `1px solid ${colors.border.light}`,
  borderRadius: radius.xl,
  padding: spacing.lg,
  boxShadow: shadows.xl,
  maxWidth: '450px',
  maxHeight: '70vh',
  overflowY: 'auto',
});

const CategorySection = styled('div')({
  marginBottom: spacing.lg,
});

const CategoryTitle = styled('h6')({
  fontSize: typography.fontSize.base,
  fontWeight: typography.fontWeight.semibold,
  color: colors.primary.main,
  marginBottom: spacing.md,
  paddingBottom: spacing.sm,
  borderBottom: `2px solid ${colors.border.light}`,
  textAlign: 'right',
});

const CategoryGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: spacing.sm,
});

const FieldOptionButton = styled('button')({
  background: colors.background.paper,
  color: colors.text.primary,
  border: `1px solid ${colors.border.light}`,
  borderRadius: radius.md,
  padding: spacing.md,
  textAlign: 'right',
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.regular,
  cursor: 'pointer',
  transition: transitions.all,
  '&:hover': {
    background: colors.state.hover,
    borderColor: colors.primary.main,
    transform: 'translateX(-2px)',
  },
});

const CancelButton = styled('button')({
  background: colors.neutral[100],
  color: colors.text.primary,
  border: `1px solid ${colors.border.main}`,
  borderRadius: radius.md,
  padding: `${spacing.sm} ${spacing.lg}`,
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.medium,
  cursor: 'pointer',
  marginTop: spacing.md,
  width: '100%',
  transition: transitions.all,
  '&:hover': {
    background: colors.neutral[200],
  },
});

// ============================================================================

export default function UpdateTemplate(props) {

  const location = useLocation();
  const currentPath = location.pathname;
  const currentProtocol = window.location.protocol;
  const currentDomain = window.location.hostname;
  const currentPort = window.location.port;

  const [inputFields, setInputFields] = useState([]);
  const [extraInputFields, setExtraInputFields] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [containerBounds, setContainerBounds] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [clickX, setClickX] = useState(null);
  const [clickY, setClickY] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const containerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageWidth, setPageWidth] = useState(null);
  const [pageHeight, setPageHeight] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [templateLink, setTemplateLink] = useState("");
  const [linkFetched, setLinkFetched] = useState(false);
  const [requireID, setRequireID] = useState(false);
  const [fileUpdated, setFileUpdated] = useState(false);
  const [fileName, setFileName] = useState("");

  // NEW UX STATE VARIABLES
  const [showFloatingPanel, setShowFloatingPanel] = useState(false);
  const [showCategorizedFields, setShowCategorizedFields] = useState(false);
  const [preSelectedFieldType, setPreSelectedFieldType] = useState(null);

  const emptySignature = useState("data:image/png;base64,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");

  const [addingTextInputField, SetAddingTextInputField] = useState(false);
  const [addingExtraTextInputField, SetAddingExtraTextInputField] = useState(false);
  const [addingSignatureInputField, SetAddingSignatureInputField] = useState(false);

  const navigate = useNavigate();
  const { key } = useParams();

  const pdfFile = `${window.AppConfig.serverDomain}/api/documentSign/${key}.pdf`;
  const fontFileUrl = 'DavidLibre-Regular.ttf';

  // Use debounced window width hook (150ms debounce) - handles mobile viewport correctly
  const windowWidth = useWindowWidth(150);

  // CATEGORIZED OPTIONS FOR FIELD TYPES
  const categorizedOptions = {
    'פרטים בסיסיים': [
      { label: 'שם הילד', value: 'childName' },
      { label: 'שם פרטי', value: 'childFirstName' },
      { label: 'שם משפחה', value: 'childLastName' },
      { label: 'מספר תעודת תלמיד', value: 'childId' }
    ],
    'תאריכים': [
      { label: 'היום', value: 'day' },
      { label: 'החודש', value: 'month' },
      { label: 'השנה', value: 'year' },
      { label: 'יום הולדת', value: 'dateOfBirth' },
      { label: 'שנה עברית', value: 'hebrewYear' },
      { label: 'תאריך התחלת הנוכחות', value: 'attendanceStartingDate' },
      { label: 'תאריך חתימה', value: 'signingDate' }
    ],
    'הורים': [
      { label: 'שם הורה 1', value: 'parentName1' },
      { label: 'מספר תעודת הורה 1', value: 'parentId1' },
      { label: 'מספר פלפון הורה 1', value: 'phoneNumber1' },
      { label: 'שם הורה 2', value: 'parentName2' },
      { label: 'מספר תעודת הורה 2', value: 'parentId2' },
      { label: 'מספר פלפון הורה 2', value: 'phoneNumber2' },
      { label: 'מקצוע הורה ראשון', value: 'parentJob1' },
      { label: 'מקצוע הורה שני', value: 'parentJob2' }
    ],
    'יצירת קשר': [
      { label: 'כתובת', value: 'address' },
      { label: 'מיקוד - ZIP', value: 'zip' },
      { label: 'מספר פלפון הורה ראשון', value: 'parentHomeNumber1' },
      { label: 'אימייל הורה ראשון', value: 'parentEmailAddress1' },
      { label: 'מספר פלפון הורה שני', value: 'parentHomeNumber2' },
      { label: 'אימייל הורה שני', value: 'parentEmailAddress2' },
      { label: 'מקום עבודה של הורה 1', value: 'parentWorkPlace1' },
      { label: 'מקום עבודה של הורה 2', value: 'parentWorkPlace2' },
      { label: 'מספר פלפון בעבודה הורה 1', value: 'parentWorkPhoneNumber1' },
      { label: 'מספר פלפון בעבודה הורה 2', value: 'parentWorkPhoneNumber2' }
    ],
    'בריאות': [
      { label: 'קופת חולים', value: 'hmo' },
      { label: 'אלרגיות', value: 'allergies' },
      { label: 'אלרגיה לתרופות', value: 'allergyToMedication' },
      { label: 'אלרגיה לאוכל', value: 'allergyToFood' },
      { label: 'מחלות קודמות', value: 'pastDiseases' },
      { label: 'האם קיימת בעיה בריאותית', value: 'healthIssueExist' },
      { label: 'בעיה בריאותית וטיפול בה', value: 'healthIssueAndSolution' },
      { label: 'האם קיבל חיסונים מלאים', value: 'receivedFullVaccination' },
      { label: 'חיסונים שלא קיבל הילד', value: 'nonReceivedVaccinations' },
      { label: 'שם רופא', value: 'doctorName' }
    ],
    'משפחה ואחים': [
      { label: 'ארץ לידה', value: 'countryOfBirth' },
      { label: 'שנת עליה', value: 'yearOfArrival' },
      { label: 'אח ראשון', value: 'brother1' },
      { label: 'אח שני', value: 'brother2' },
      { label: 'אח שלישי', value: 'brother3' },
      { label: 'אח רביעי', value: 'brother4' },
      { label: 'מספר סידורי של הילד בין אחיו', value: 'childOrderBetweenSiblings' }
    ],
    'קרובי משפחה וחירום': [
      { label: 'שם קרוב משפחה ראשון', value: 'relativeName1' },
      { label: 'סטטוס קרבות משפחה ראשון', value: 'relativeStatus1' },
      { label: 'מספר פ. קרוב משפחה ראשון', value: 'relativeNumber1' },
      { label: 'שם קרוב משפחה שני', value: 'relativeName2' },
      { label: 'סטטוס קרבות משפחה שני', value: 'relativeStatus2' },
      { label: 'מספר פ. קרוב משפחה שני', value: 'relativeNumber2' },
      { label: 'שם איש קשר למצב חירום', value: 'emergencyContactName' },
      { label: 'מס איש קשר למצב חירום', value: 'emergencyContactNumber' }
    ],
    'גן וחינוך': [
      { label: 'שם גן', value: 'kindergarten' },
      { label: 'שם כיתה', value: 'className' },
      { label: 'גן קודם', value: 'previousKindergarten' }
    ],
    'תשלומים': [
      { label: 'סכום תשלום חודשה', value: 'monthlyPayment' },
      { label: 'אמצעי תשלום', value: 'paymentMethod' }
    ],
    'הרשאות': [
      { label: 'האם ההורים מאשרים לצילום הילד פנימי', value: 'allowsPhotographingInternal' },
      { label: 'האם ההורים מאשרים לצילום הילד חוץ', value: 'allowsPhotographingExternal' }
    ],
    'חתימות': [
      { label: 'חתימה 1', value: 'signature1' },
      { label: 'חתימה 2', value: 'signature2' }
    ],
    'אחר': [
      { label: 'שם החותם', value: 'approverName' },
      { label: 'סטטוס קרבות החותם', value: 'approverStatus' },
      { label: 'כתובת החותם', value: 'approverAddress' },
      { label: 'מס. פלפון החותם', value: 'approverPhoneNumber' },
      { label: 'מ- (כתובת)', value: 'from' },
      { label: 'הערות', value: 'remarks' },
      { label: 'אחר', value: 'other' }
    ]
  };

  const quickAddFields = [
    { label: 'שם הילד', value: 'childName' },
    { label: 'תאריך לידה', value: 'dateOfBirth' },
    { label: 'חתימה 1', value: 'signature1' },
    { label: 'טלפון הורה 1', value: 'phoneNumber1' },
    { label: 'כתובת', value: 'address' }
  ];

  // NEW UX FUNCTIONS
  const handleQuickAddField = (fieldOption) => {
    setPreSelectedFieldType(fieldOption);
    SetAddingTextInputField(true);
    setShowFloatingPanel(false);
    setShowCategorizedFields(false);
  };

  const handleCancelAdding = () => {
    SetAddingTextInputField(false);
    SetAddingExtraTextInputField(false);
    SetAddingSignatureInputField(false);
    setPreSelectedFieldType(null);
    setShowFloatingPanel(false);
    setShowCategorizedFields(false);
  };

  const handleFileChange = (event) => {
    setFileUpdated(true);
    const file = event.target.files[0];
    setSelectedFile(file);
    setFileName(file.name);
  };

  // Update container bounds when window resizes
  useEffect(() => {
    setContainerBounds(containerRef.current?.getBoundingClientRect());
  }, [windowWidth]);

  useEffect(() => {
    const fetchNumPages = async () => {
      try {
        // Window width is now handled by useWindowWidth hook

        const pdf = await pdfjs.getDocument(pdfFile).promise;
        setNumPages(pdf.numPages);

        const firstPage = await pdf.getPage(1);
        const { width, height } = firstPage.getViewport({ scale: 1 });

        console.log([width, height]);
        setPageWidth(width);
        setPageHeight(height);

      } catch (error) {
        console.error('Error occurred while fetching PDF:', error);
      }
    };

    fetchNumPages();
  }, [pdfFile]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setSelectedFile(`${window.AppConfig.serverDomain}/api/documentSign/${key}.pdf`);
        const response = await axios.get(`${window.AppConfig.serverDomain}/api/organzations/document-input-fields/${key}`);
        setInputFields(response.data.inputFields);
        if (response.data.requireID === true) {
          setRequireID(response.data.requireID);
        }
        setFileName(response.data.templateName)
        console.log(response.data.inputFields);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Only run for File objects (user uploaded a new file via file picker).
    // URL strings are already handled by the pdfjs useEffect above.
    if (!selectedFile || typeof selectedFile === 'string') return;

    const fetchNumPages = async () => {
      try {
        const existingPdfBytes = await selectedFile.arrayBuffer();
        const pdfDoc = await PDFLibDocument.load(existingPdfBytes);
        setNumPages(pdfDoc.getPageCount());

        const page = pdfDoc.getPage(0);
        const { width, height } = page.getSize();

        setPageWidth(width);
        setPageHeight(height);

      } catch (error) {
        console.error('Error occurred while fetching PDF:', error);
      }
    };

    fetchNumPages();
  }, [selectedFile]);

  const handleDrag = (e, data, index) => {
    const x = data.x;
    const y = data.y;
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields];
      updatedInputFields[index].x = (x / windowWidth);
      // y stores field TOP normalized (data.y = element top from react-draggable)
      const renderedHeight = windowWidth * ((pageHeight) / pageWidth);
      const savedY = y / renderedHeight;
      updatedInputFields[index].y = savedY;
      const fieldHeight = windowWidth * updatedInputFields[index].editor.height / 40;
      console.log(`[DRAG SAVE] field="${updatedInputFields[index].editor?.inputType?.value}" drag_top=${y} fieldHeight=${fieldHeight} renderedHeight=${renderedHeight} saved_y=${savedY} field_top_px=${y} field_bottom_px=${y + fieldHeight}`);
      return updatedInputFields;
    });
  };

  const handlePageClick = (event, inputType) => {
    const containerBounds = containerRef.current.getBoundingClientRect();
    const containerX = containerBounds.left;
    const containerY = containerBounds.top;

    setContainerBounds(containerBounds);

    const containerwidthX = containerBounds.right - containerBounds.left;
    const containerwidthY = containerBounds.bottom - containerBounds.top;

    const clickXtemp = ((event.clientX - containerX) / containerwidthX);
    const clickYtemp = ((event.clientY - containerY) / containerwidthY);

    setClickX(clickXtemp);
    setClickY(clickYtemp);

    if (inputType === 1) {
      const newInputField = {
        x: clickXtemp,
        y: clickYtemp,
        value: "",
        page: currentPage,
        isCursor: false,
        options: [],
        mandatory: false,
        editor: {
          state: false,
          width: 0.25,
          height: 1,
          font: 1,
          inputType: preSelectedFieldType
            ? { ...preSelectedFieldType, value: preSelectedFieldType.value === 'other' ? 'other' + generateRandomString(8) : preSelectedFieldType.value }
            : { label: 'אחר', value: 'other' + generateRandomString(8) },
        }
      };

      setInputFields([...inputFields.filter(field => !field.isCursor), newInputField]);
      SetAddingTextInputField(false);
      setPreSelectedFieldType(null);

    } else if (inputType === 2) {
      const newExtraInputField = {
        x: clickXtemp,
        y: clickYtemp,
        value: "data:image/png;base64,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",
        page: currentPage,
        isCursor: false,
        editor: {
          state: false,
          width: 1,
          height: 1,
          font: 1,
          inputType: { label: 'חתימה', value: 'signature1' },
        }
      };

      setExtraInputFields([...extraInputFields.filter(field => !field.isCursor), newExtraInputField]);
      SetAddingExtraTextInputField(false);
    }

    console.log(signatures);
  };

  const generateRandomString = (length) => [...Array(length)].map(() => (~~(Math.random() * 36)).toString(36)).join('');

  const handleMouseMove = (event, inputType) => {
    const containerBounds = containerRef.current.getBoundingClientRect();
    const containerX = containerBounds.left;
    const containerY = containerBounds.top;

    setContainerBounds(containerBounds);

    const containerwidthX = containerBounds.right - containerBounds.left;
    const containerwidthY = containerBounds.bottom - containerBounds.top;

    const clickXtemp = ((event.clientX - containerX) / containerwidthX);
    const clickYtemp = ((event.clientY - containerY) / containerwidthY);

    setClickX(clickXtemp);
    setClickY(clickYtemp);

    const newClick = { x: clickXtemp * pageWidth, y: clickYtemp * pageHeight };
    setClicks([...clicks, newClick]);

    if (inputType === 1) {
      const newInputField = {
        x: clickXtemp,
        y: clickYtemp,
        value: '',
        page: currentPage,
        isCursor: true,
        options: [],
        mandatory: false,
        editor: {
          state: false,
          width: 0.25,
          height: 1,
          font: 1,
          inputType: preSelectedFieldType
            ? { ...preSelectedFieldType, value: preSelectedFieldType.value === 'other' ? 'other' + generateRandomString(8) : preSelectedFieldType.value }
            : { label: 'אחר', value: 'other' + generateRandomString(8) },
        }
      };

      setInputFields([...inputFields.filter(field => !field.isCursor), newInputField]);
      console.log([window.scrollX, window.scrollY, inputFields.find(field => field.isCursor)]);
    } else if (inputType === 2) {
      const newExtraInputField = {
        x: clickXtemp,
        y: clickYtemp,
        value: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAVQAAADICAYAAAC3QRk5AAAAAXNSR0IArs4c6QAABmJJREFUeF7t1DENADAMBLEEQPnTrVQKvdEB8IMV3c7MGUeAAAEC3wIrqN+GBggQIPAEBNUjECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIELjoXCvGAGlIAAAAAAElFTkSuQmCC",
        page: currentPage,
        isCursor: false,
        editor: {
          state: false,
          width: 1,
          height: 1,
          font: 1,
          inputType: { label: 'חתימה', value: 'signature1' },
        }
      };

      setExtraInputFields([...extraInputFields.filter(field => !field.isCursor), newExtraInputField]);
      console.log([window.scrollX, window.scrollY, inputFields.find(field => field.isCursor)]);
    }
  };

  const handleInputChange = (event, index) => {
    console.log(inputFields[index].editor.inputType.label);
    const updatedInputFields = [...inputFields];
    inputFields.forEach((field, index2) => {
      if (field.editor.inputType.value === inputFields[index].editor.inputType.value) {
        updatedInputFields[index2].value = event.target.value;
      }
    })
    setInputFields(updatedInputFields);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setClickY(null);
  };

  const handleAddingTextInputField = () => {
    SetAddingTextInputField(true);
  }

  const handleAddingExtraTextInputField = () => {
    SetAddingExtraTextInputField(true);
  }

  const handleAddingSignatureInputField = () => {
    console.log(addingSignatureInputField);
    SetAddingSignatureInputField(true);
  }

  const handleMouseEnter = (event, index) => {
    if (!signatureOpen) {
      setHoveredIndex(index);
    }
  };

  const handleMouseLeave = () => {
    if (!signatureOpen) {
      setHoveredIndex(null);
    }
  };

  const switchEditorState = (index) => {
    const fieldToEdit = inputFields[index];
    fieldToEdit.editor.state = !fieldToEdit.editor.state;
    setInputFields([...inputFields.filter((field, liveIndex) => liveIndex !== index), fieldToEdit]);
  }

  const deleteField = (index) => {
    setInputFields([...inputFields.filter((field, liveIndex) => liveIndex !== index)]);
  }

  const handleSliderChange = (event, newValue, index) => {
    console.log([index, newValue]);
    setInputWidth(newValue, index);
  };

  const setInputWidth = (newValue, index) => {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields];
      updatedInputFields[index].editor.width = newValue / 100;
      return updatedInputFields;
    });
  };

  const handleDragStart = (event) => {
    event.preventDefault();
  };

  const handleDragMove = (event, index) => {
    const delta = event.movementY;
    if (delta > 0) {
      setInputWidth(inputFields[index].filter.width * 100 - 1, index);
    } else if (delta < 0) {
      setInputWidth(inputFields[index].filter.width * 100 + 1, index);
    }
  };

  const handleHeightSliderChange = (event, newValue, index) => {
    console.log([index, newValue]);
    setInputHeight(newValue, index);
  };

  const handleHeightDragStart = (event) => {
    event.preventDefault();
  };

  const setInputHeight = (newValue, index) => {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields];
      updatedInputFields[index].editor.height = newValue / 100;
      return updatedInputFields;
    });
  };

  const handleHeightDragMove = (event, index) => {
    const delta = event.movementY;
    if (delta > 0) {
      setInputWidth(inputFields[index].filter.height * 100 - 1, index);
    } else if (delta < 0) {
      setInputWidth(inputFields[index].filter.height * 100 + 1, index);
    }
  };

  const handleIndexChange = (event, index, coordinateType) => {
    console.log(inputFields[index]);
    const newValue = event.target.value;
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields];
      if (coordinateType === 'x') {
        updatedInputFields[index].x = ((newValue < windowWidth) ? newValue : windowWidth) / windowWidth;
      } else {
        updatedInputFields[index].y = ((newValue < (windowWidth * ((pageHeight) / pageWidth))) ? newValue : (windowWidth * ((pageHeight) / pageWidth))) / (windowWidth * ((pageHeight) / pageWidth));
        console.log(`[DRAG SAVE] field="${updatedInputFields[index].editor?.inputType?.value}" raw_y=${newValue} windowWidth=${windowWidth} pageHeight=${pageHeight} pageWidth=${pageWidth} saved_y=${updatedInputFields[index].y}`);
      }
      return updatedInputFields;
    });
  }

  const handleInputTypeChange = (event, value, index) => {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields];
      updatedInputFields[index].editor.inputType = value;
      if (value.value === 'signature1' || value.value === 'signature2') {
        updatedInputFields[index].value = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAVQAAADICAYAAAC3QRk5AAAAAXNSR0IArs4c6QAABmJJREFUeF7t1DENADAMBLEEQPnTrVQKvdEB8IMV3c7MGUeAAAEC3wIrqN+GBggQIPAEBNUjECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIELjoXCvGAGlIAAAAAAElFTkSuQmCC";
      } else if (value.value === 'other') {
        updatedInputFields[index].editor.inputType.value = value.value + generateRandomString(8);
        updatedInputFields[index].value = "";
      } else {
        updatedInputFields[index].value = "";
      }
      console.log(updatedInputFields);
      return updatedInputFields;
    });
  }

  function updateSignature(index, url) {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields];
      inputFields.forEach((field, index2) => {
        if (field.editor.inputType.value === inputFields[index].editor.inputType.value) {
          updatedInputFields[index2].value = url;
        }
      })
      return updatedInputFields;
    });
  }

  async function saveInputFields() {
    setLoading(true);
    await handleSubmit();
    setLoading(false);
  }

  async function handlefileNotUpdatedsubmit() {
    let localUrl = `${window.AppConfig.serverDomain}/api/organzations/update-input-fields/`;

    const formData = {
      data: JSON.stringify(inputFields),
      templateID: key.split('_').pop(),
      requireID: requireID,
    };

    try {
      const response = await axios.post(localUrl, formData);
      console.log(response);
      setLinkFetched(true);
    } catch (error) {
      console.error('Error:', error);
    }
  }

  async function handlefileUpdatedSubmit() {
    let localUrl = `${window.AppConfig.serverDomain}/api/organzations/update-template/`;

    const data = new FormData();
    data.append('pdf', selectedFile);
    data.append('templateName', selectedFile.name);
    data.append('data', JSON.stringify(inputFields));
    data.append('templateID', key.split('_').pop());
    data.append('requireID', requireID);

    try {
      const response = await axios.post(localUrl, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setLinkFetched(true);
    } catch (error) {
      console.error('Error:', error);
    }
  }

  const handleSubmit = async () => {
    if (fileUpdated) {
      handlefileUpdatedSubmit();
    } else {
      handlefileNotUpdatedsubmit();
    }

    if (fileUpdated) {
      setTemplateLink(`${currentProtocol}//${currentDomain}/template/${key.split('_').pop()}`);
    } else {
      setTemplateLink(`${currentProtocol}//${currentDomain}/template/${key.split('_').pop()}`);
    }
    setLinkFetched(true);
  };

  function removeAfterLastUnderscore(str) {
    const lastUnderscoreIndex = str.lastIndexOf('_');
    if (lastUnderscoreIndex !== -1) {
      return str.substring(0, lastUnderscoreIndex);
    }
    return str;
  }

  function handlePdfLoadSuccess() {
    // windowWidth is managed by useWindowWidth hook (line 330)
  }

  const handleCheckboxChange = (event) => {
    setRequireID(event.target.checked);
    console.log(event.target.checked);
  };

  const handleAddOption = (event, index) => {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields];
      updatedInputFields[index].options.push("");
      console.log(updatedInputFields[index].options);
      return updatedInputFields;
    });
  }

  const handleOptionChange = (e, index, optionIndex) => {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields];
      updatedInputFields[index].options[optionIndex] = e.target.value;
      console.log(updatedInputFields[index].options[optionIndex]);
      return updatedInputFields;
    });
  }

  const handleSelectChange = (event, index) => {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields];
      updatedInputFields[index].value = event.target.value;
      console.log(updatedInputFields[index].value);
      return updatedInputFields;
    });
  }

  const handleMandatoryChange = (event, index) => {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields];
      updatedInputFields[index].mandatory = !updatedInputFields[index].mandatory;
      console.log(updatedInputFields[index].mandatory);
      return updatedInputFields;
    });
  }

  return (
    <>
      {/* Enhanced Header */}
      <PageHeader>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <HeaderTitle>
                עורך תבניות PDF
              </HeaderTitle>
              <HeaderSubtitle>
                צור ועדכן טפסים אינטראקטיביים בקלות
              </HeaderSubtitle>
            </div>
            <div className="col-md-4 text-end">
              {fileName && (
                <FileInfoCard>
                  <FileInfoLabel>קובץ נוכחי:</FileInfoLabel>
                  <FileInfoText>{fileName}</FileInfoText>
                </FileInfoCard>
              )}
            </div>
          </div>
        </div>
      </PageHeader>

      {/* Enhanced File Upload Section */}
      <div className="container mb-4">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <UploadSection
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file && file.type === 'application/pdf') {
                  setFileUpdated(true);
                  setSelectedFile(file);
                  setFileName(file.name);
                }
              }}>
              <UploadIcon>
                📄
              </UploadIcon>
              <UploadLabel htmlFor="formFile">
                בחר או גרור קובץ PDF
              </UploadLabel>
              <input
                className="form-control"
                id="formFile"
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <UploadHelpText>
                תומך בקבצי PDF בלבד • גודל מקסימלי: 10MB
              </UploadHelpText>
            </UploadSection>
          </div>
        </div>
      </div>

      {/* Enhanced Floating Control Panel */}
      {selectedFile && (
        <>
          <FloatingControlPanel>

            {/* Enhanced Quick Add Panel */}
            {showFloatingPanel && (
              <QuickAddPanel>
                <PanelHeader>
                  <PanelIcon>⚡</PanelIcon>
                  <PanelTitle>
                    הוסף שדה מהיר
                  </PanelTitle>
                </PanelHeader>
                <QuickAddGrid>
                  {quickAddFields.map(field => (
                    <QuickAddButton
                      key={field.value}
                      onClick={() => handleQuickAddField(field)}
                    >
                      {field.label}
                    </QuickAddButton>
                  ))}
                  <ViewAllFieldsButton
                    onClick={() => {
                      setShowFloatingPanel(false);
                      setShowCategorizedFields(true);
                    }}
                  >
                    🔍 עוד שדות...
                  </ViewAllFieldsButton>
                </QuickAddGrid>
              </QuickAddPanel>
            )}

            {/* Enhanced Categorized Fields Panel */}
            {showCategorizedFields && (
              <div style={{
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 25px 50px rgba(0,0,0,0.15), 0 10px 30px rgba(102, 126, 234, 0.2)',
                maxWidth: '480px',
                maxHeight: '500px',
                overflowY: 'auto',
                backdropFilter: 'blur(15px)'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  borderBottom: '2px solid #f0f0f0',
                  paddingBottom: '15px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ fontSize: '1.5rem', marginRight: '10px' }}>🎯</div>
                    <h5 style={{
                      margin: 0,
                      textAlign: 'right',
                      fontSize: '1.3rem',
                      fontWeight: '600',
                      color: '#333'
                    }}>
                      כל השדות
                    </h5>
                  </div>
                  <button
                    onClick={() => setShowCategorizedFields(false)}
                    style={{
                      background: 'linear-gradient(135deg, #ff6b6b, #ee5a5a)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      fontSize: '16px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    ×
                  </button>
                </div>

                {Object.entries(categorizedOptions).map(([category, fields]) => (
                  <div key={category} style={{ marginBottom: '24px' }}>
                    <h6 style={{
                      margin: '0 0 12px 0',
                      textAlign: 'right',
                      borderBottom: '2px solid rgba(19, 36, 161, 0.1)',
                      paddingBottom: '8px',
                      fontSize: '1rem',
                      fontWeight: '700',
                      color: colors.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                        width: '4px',
                        height: '20px',
                        borderRadius: '2px'
                      }}></span>
                      {category}
                    </h6>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: '8px'
                    }}>
                      {fields.map(field => (
                        <button
                          key={field.value}
                          onClick={() => handleQuickAddField(field)}
                          style={{
                            background: 'linear-gradient(135deg, #f8f9ff 0%, #e8edff 100%)',
                            color: '#667eea',
                            border: '1px solid rgba(102, 126, 234, 0.2)',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            textAlign: 'right',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #667eea, #764ba2)';
                            e.target.style.color = 'white';
                            e.target.style.transform = 'scale(1.02)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #f8f9ff 0%, #e8edff 100%)';
                            e.target.style.color = '#667eea';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          {field.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Enhanced Main Control Buttons */}
            <div style={{
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
              border: '1px solid rgba(102, 126, 234, 0.3)',
              borderRadius: '25px',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 15px 35px rgba(0,0,0,0.1), 0 5px 15px rgba(102, 126, 234, 0.2)',
              backdropFilter: 'blur(10px)'
            }}>
              {!addingTextInputField && !addingExtraTextInputField && !addingSignatureInputField ? (
                <>
                  <button
                    onClick={() => setShowFloatingPanel(!showFloatingPanel)}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '12px 24px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                    }}
                  >
                    <AddIcon style={{ fontSize: '18px' }} />
                    הוסף שדה
                  </button>
                  <button
                    onClick={() => setShowCategorizedFields(!showCategorizedFields)}
                    style={{
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '10px 20px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    כל השדות
                  </button>
                </>
              ) : (
                <button
                  onClick={handleCancelAdding}
                  style={{
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '12px 24px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(255, 107, 107, 0.4)'
                  }}
                >
                  ✕ ביטול הוספה
                </button>
              )}
            </div>
          </FloatingControlPanel>
        </>
      )}

      <div ref={containerRef} style={{ width: '100%', overflow: 'visible', position: 'relative' }} onMouseMove={addingTextInputField ? (event) => handleMouseMove(event, 1) : addingExtraTextInputField ? (event) => handleMouseMove(event, 2) : null}>
        <Document file={selectedFile}  >
          <div style={{ pointerEvents: `${addingTextInputField || addingSignatureInputField ? 'auto' : 'none'}` }} >
            <Page onLoadSuccess={handlePdfLoadSuccess} key={currentPage - 1} pageNumber={currentPage} onClick={addingTextInputField ? (event) => handlePageClick(event, 1) : addingExtraTextInputField ? (event) => handlePageClick(event, 2) : null} width={containerRef.current?.clientWidth} />
          </div>
        </Document>

        {inputFields.map((inputField, index) => (
          (inputField.page === currentPage) ?
            <>
              {
                inputField.editor.inputType.value === 'signature1' || inputField.editor.inputType.value === 'signature2' ? (
                  <div key={index}
                    style={{
                      opacity: `${hoveredIndex === index || inputField.editor.state ? 0.8 : 1}`,
                      position: 'absolute', top: inputField.y * (windowWidth) * ((pageHeight) / pageWidth), left: inputField.x * (windowWidth),
                      fontSize: `${windowWidth / 40}px`,
                      width: `${(windowWidth * inputField.editor.width / 1.5)}px`,
                      padding: '4px',
                    }}
                    onMouseEnter={(event) => handleMouseEnter(event, index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <SignatureModal key={index} windowWidth={windowWidth} setHoveredIndex={setHoveredIndex} setSignatureOpen={setSignatureOpen} updateSignature={updateSignature} url={inputField.value} index={index} signer={"חתימה ראשונה"} />
                  </div>) : (<Draggable onDrag={(e, data) => handleDrag(e, data, index)} position={{ x: inputField.x * (windowWidth), y: inputField.y * (windowWidth) * ((pageHeight) / pageWidth) }} >
                    <div style={{ position: 'absolute', top: 0, left: 0 }}>
                      {inputField?.options?.length === 0 ? <input
                        key={index}
                        type="text"
                        value={inputField.value}
                        onChange={(event) => handleInputChange(event, index)}
                        style={{
                          opacity: `${hoveredIndex === index || inputField.editor.state ? 0.8 : 1}`,
                          width: `${(windowWidth * inputField.editor.width / 1.5)}px`,
                          height: `${windowWidth * inputField.editor.height / 40}px`,
                          fontSize: `${windowWidth / 60}px`,
                          padding: '4px',
                        }}
                        onMouseEnter={(event) => handleMouseEnter(event, index)}
                        onMouseLeave={handleMouseLeave}
                      /> :
                        <Select
                          style={{
                            opacity: `${hoveredIndex === index || inputField.editor.state ? 0.8 : 1}`,
                            width: `${(windowWidth * inputField.editor.width / 1.5)}px`,
                            height: `${windowWidth * inputField.editor.height / 40}px`,
                            fontSize: `${windowWidth / 60}px`,
                            padding: '4px',
                          }}
                          value={inputField.value}
                          onChange={(event) => handleSelectChange(event, index)}
                          displayEmpty
                          fullWidth
                          onMouseEnter={(event) => handleMouseEnter(event, index)}
                          onMouseLeave={handleMouseLeave}
                        >
                          <MenuItem value="">
                            -- בחר אופציה --
                          </MenuItem>
                          {inputField.options.map((input, inputIndex) => (
                            <MenuItem key={inputIndex} value={input}>
                              {input || 'בחר אופציה'}
                            </MenuItem>
                          ))}
                        </Select>}
                    </div>
                  </Draggable>)
              }

              {hoveredIndex === index && (
                <FaEdit
                  onMouseEnter={(event) => handleMouseEnter(event, index)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => switchEditorState(index)}
                  style={{
                    height: `${windowWidth / 40}px`, marginLeft: `${windowWidth / 200}px`, cursor: 'pointer', position: 'absolute', top: (inputField.y * (windowWidth) * ((pageHeight) / pageWidth)), left: (inputField.x * (windowWidth)),
                  }} />
              )}

              {inputField.editor.state && (
                <div className="pt-3" style={{
                  position: 'absolute',
                  top: (inputField.y * (windowWidth) * ((pageHeight) / pageWidth)) + windowWidth / 40,
                  left: (inputField.x * (windowWidth)),
                  width: `${windowWidth > 700 ? windowWidth / 4 : windowWidth / 2}px`
                }}>
                  <div className="card p-1" >
                    <h6 style={{ fontSize: `${windowWidth / 60}px` }}>רוחב:</h6>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        userSelect: 'none',
                        cursor: 'grab',
                      }}
                      className=""
                      draggable
                      onDragStart={handleDragStart}
                      onDrag={(event) => handleDragMove(event, index)}
                    >
                      <Typography variant="body1" inputProps={{
                        style: {
                          fontSize: `${windowWidth / 50}px`,
                        },
                      }}>{Math.trunc(inputField.editor.width * 100)}</Typography>
                      <Slider
                        value={inputField.editor.width * 100}
                        min={5}
                        max={100}
                        step={1}
                        size="small"
                        aria-label="Small"
                        onChange={(event, newValue) => handleSliderChange(event, newValue, index)}
                      />
                    </div>
                    <h6 style={{ fontSize: `${windowWidth / 60}px` }}>אורך:</h6>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        userSelect: 'none',
                        cursor: 'grab',
                      }}
                      className=""
                      draggable
                      onDragStart={handleHeightDragStart}
                      onDrag={(event) => handleHeightDragMove(event, index)}
                    >
                      <Typography variant="body1" inputProps={{
                        style: {
                          fontSize: `${windowWidth / 50}px`,
                        },
                      }}>{Math.trunc(inputField.editor.height * 100)}</Typography>
                      <Slider
                        value={inputField.editor.height * 100}
                        min={5}
                        max={100}
                        step={1}
                        size="small"
                        aria-label="Small"
                        onChange={(event, newValue) => handleHeightSliderChange(event, newValue, index)}
                      />
                    </div>

                    <Autocomplete
                      className="mb-2"
                      disablePortal
                      options={options}
                      onChange={(event, value) => handleInputTypeChange(event, value, index)}
                      value={inputField.editor.inputType}
                      renderInput={(params) => <TextField {...params} label="סוג קלט" />}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon className="mx-2" />}
                      onClick={(event) => handleAddOption(event, index)}
                    >
                      הוסף אופציה קבועה
                    </Button>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                    >
                      {inputField.options.map((input, optionIndex) => (
                        <TextField
                          key={optionIndex}
                          value={inputField.options[optionIndex]}
                          onChange={e => handleOptionChange(e, index, optionIndex)}
                          placeholder="כתוב את האופציה"
                          fullWidth
                        />
                      ))}
                    </Box>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={inputField.mandatory}
                          onChange={(event) => handleMandatoryChange(event, index)}
                          color="primary"
                        />
                      }
                      label="שדה חובה"
                    />

                    <div className="row">
                      <div className="col col-7 d-flex justify-content-center ps-1">
                        <button className="btn btn-success w-100" style={{ fontSize: `${windowWidth / 60}px` }} onClick={() => switchEditorState(index)}> Save</button>
                      </div>
                      <div className="col col-5 d-flex justify-content-center pe-1">
                        <button className="btn btn-danger w-100" style={{ fontSize: `${windowWidth / 60}px` }} onClick={() => deleteField(index)}> Delete</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </> : <></>
        ))}
      </div>

      {selectedFile && <div style={{ marginBottom: '6rem' }}>
        {/* Enhanced Page Navigation */}
        <div style={{
          background: 'linear-gradient(135deg, #f8f9ff 0%, #e8edff 100%)',
          border: '1px solid rgba(102, 126, 234, 0.2)',
          borderRadius: '12px',
          padding: '20px',
          margin: '20px auto',
          maxWidth: '600px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '20px',
            marginBottom: '20px'
          }}>
            {(currentPage !== 1) && (
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
              >
                <KeyboardArrowRightIcon />
              </button>
            )}

            {(numPages > 1) && (
              <div style={{
                background: 'white',
                border: '2px solid #667eea',
                borderRadius: '25px',
                padding: '10px 25px',
                fontSize: '1.2rem',
                fontWeight: '700',
                color: '#667eea',
                minWidth: '80px',
                textAlign: 'center'
              }}>
                {currentPage} / {numPages}
              </div>
            )}

            {(currentPage !== numPages) && (
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
              >
                <KeyboardArrowLeftIcon />
              </button>
            )}
          </div>

          {/* Enhanced Settings Section */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '30px',
            marginBottom: '20px'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={requireID}
                  onChange={handleCheckboxChange}
                  sx={{
                    color: '#667eea',
                    '&.Mui-checked': {
                      color: '#667eea',
                    },
                  }}
                />
              }
              label={
                <span style={{
                  fontSize: '1rem',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  דרוש תעודה מזהה
                </span>
              }
            />
          </div>

          {/* Enhanced Save Button */}
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={saveInputFields}
              disabled={loading}
              style={{
                background: loading ?
                  'linear-gradient(135deg, #ccc, #999)' :
                  'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '25px',
                padding: '15px 40px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: loading ? 'none' : '0 6px 20px rgba(76, 175, 80, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                margin: '0 auto'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(76, 175, 80, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 6px 20px rgba(76, 175, 80, 0.3)';
                }
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #ffffff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  שומר...
                </>
              ) : (
                <>
                  💾 שמור שינויים
                </>
              )}
            </button>
          </div>
        </div>

        {/* Enhanced Loading Overlay */}
        {loading &&
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(8px)'
          }}>
            <div style={{
              background: 'white',
              padding: '40px',
              borderRadius: '20px',
              textAlign: 'center',
              boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
              maxWidth: '300px'
            }}>
              <InlineSpinner size="xl" />
              <p style={{
                marginTop: '20px',
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#333'
              }}>
                שומר את השינויים...
              </p>
            </div>
          </div>}

        {/* Enhanced Success Modal */}
        {(linkFetched && templateLink !== "") && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
              borderRadius: '20px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '25px 30px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={{ fontSize: '2rem' }}>🎉</div>
                  <h3 style={{
                    margin: 0,
                    fontSize: '1.5rem',
                    fontWeight: '600'
                  }}>
                    הטופס נשמר בהצלחה!
                  </h3>
                </div>
                <button
                  onClick={() => setLinkFetched(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    fontSize: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{
                padding: '30px',
                direction: 'rtl',
                textAlign: 'right'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #e8f5e8 0%, #f0f8f0 100%)',
                  border: '1px solid #4CAF50',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '25px'
                }}>
                  <SimpleSnackbar templateLink={templateLink} name={templateLink} />
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #fff4e6 0%, #fef7ed 100%)',
                  border: '1px solid #f59e0b',
                  borderRadius: '12px',
                  padding: '20px',
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  color: '#333'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                    <div style={{ fontSize: '1.5rem' }}>📋</div>
                    <strong style={{ fontSize: '1.1rem' }}>איך זה עובד:</strong>
                  </div>
                  <p style={{ margin: '0 0 10px 0' }}>
                    הקישור לעיל מספק גרסת המסמך שניתן למלא ולחתום על ידי הלקוח.
                  </p>
                  <p style={{ margin: '0 0 10px 0' }}>
                    באפשרותך לשתף קישור זה בקלות עם הלקוחות שלך.
                  </p>
                  <p style={{ margin: '0' }}>
                    כאשר הלקוחות משלימים את המסמך על ידי מילוי הפרטים הנדרשים וחתימתו, העותק המלא של המסמך, יחד עם השדות שהוזנו, נשמר באופן אוטומטי בלוח ההגשות שלך.
                  </p>
                </div>
              </div>
              <div style={{
                background: '#f8f9ff',
                borderTop: '1px solid #e0e0e0',
                padding: '20px 30px',
                textAlign: 'center'
              }}>
                <button
                  onClick={() => setLinkFetched(false)}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '25px',
                    padding: '12px 30px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  סגור
                </button>
              </div>
            </div>
          </div>
        )}
      </div>}

      {/* Add keyframes for loading animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

const styles = StyleSheet.create({
  text: {
    position: 'absolute',
    fontSize: 12,
  },
  textContainer: {
    position: 'absolute',
    border: '1px solid black',
    padding: '5px',
  },
  input: {
    width: '100%',
    height: '100%',
    fontSize: '14px',
    padding: '4px'
  }
});

const BootstrapButton = styled(Button)({
  boxShadow: 'none',
  textTransform: 'none',
  fontSize: 16,
  padding: '6px 12px',
  border: '1px solid',
  lineHeight: 1.5,
  backgroundColor: '#0063cc',
  borderColor: '#0063cc',
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  '&:hover': {
    backgroundColor: '#0069d9',
    borderColor: '#0062cc',
    boxShadow: 'none',
  },
  '&:active': {
    boxShadow: 'none',
    backgroundColor: '#0062cc',
    borderColor: '#005cbf',
  },
  '&:focus': {
    boxShadow: '0 0 0 0.2rem rgba(0,123,255,.5)',
  },
});

const options = [
  { label: 'שם הילד', value: 'childName' },
  { label: 'מספר תעודת תלמיד', value: 'childId' },
  { label: 'חתימה 1', value: 'signature1' },
  { label: 'חתימה 2', value: 'signature2' },
  { label: 'היום', value: 'day' },
  { label: 'החודש', value: 'month' },
  { label: 'השנה', value: 'year' },
  { label: 'שם הורה 1', value: 'parentName1' },
  { label: 'מספר תעודת הורה 1', value: 'parentId1' },
  { label: 'מספר פלפון הורה 1', value: 'phoneNumber1' },
  { label: 'שם הורה 2', value: 'parentName2' },
  { label: 'מספר תעודת הורה 2', value: 'parentId2' },
  { label: 'מספר פלפון הורה 2', value: 'phoneNumber2' },
  { label: 'שם גן', value: 'kindergarten' },
  { label: 'שנה עברית', value: 'hebrewYear' },
  { label: 'שם ילד פרטי', value: 'childFirstName' },
  { label: 'שם משפחה', value: 'childLastName' },
  { label: 'יום הולדת', value: 'dateOfBirth' },
  { label: 'ארץ לידה', value: 'countryOfBirth' },
  { label: 'שנת עליה', value: 'yearOfArrival' },
  { label: 'כתובת', value: 'address' },
  { label: 'מיקוד - ZIP', value: 'zip' },
  { label: 'תאריך לידה', value: 'dateOfBirth' },
  { label: 'אח ראשון', value: 'brother1' },
  { label: 'אח שני', value: 'brother2' },
  { label: 'אח שלישי', value: 'brother3' },
  { label: 'אח רביעי', value: 'brother4' },
  { label: 'מקצוע הורה ראשון', value: 'parentJob1' },
  { label: 'מספר פלפון הורה ראשון', value: 'parentHomeNumber1' },
  { label: 'אימייל הורה ראשון', value: 'parentEmailAddress1' },
  { label: 'מקצוע הורה שני', value: 'parentJob2' },
  { label: 'מספר פלפון הורה שני', value: 'parentHomeNumber2' },
  { label: 'אימייל הורה שני', value: 'parentEmailAddress2' },
  { label: 'שם קרוב משפחה ראשון', value: 'relativeName1' },
  { label: 'סטטוס קרבות משפחה ראשון', value: 'relativeStatus1' },
  { label: 'מספר פ. קרוב משפחה ראשון', value: 'relativeNumber1' },
  { label: 'שם קרוב משפחה שני', value: 'relativeName2' },
  { label: 'סטטוס קרבות משפחה שני', value: 'relativeStatus2' },
  { label: 'מספר פ. קרוב משפחה שני', value: 'relativeNumber2' },
  { label: 'האם קיימת בעיה בריאותית', value: 'healthIssueExist' },
  { label: 'בעיה בריאותית וטיפול בה', value: 'healthIssueAndSolution' },
  { label: 'אלרגיה לתרופות', value: 'allergyToMedication' },
  { label: 'אלרגיה לאוכל', value: 'allergyToFood' },
  { label: 'מחלות קודמות', value: 'pastDiseases' },
  { label: 'אלרגיות', value: 'allergies' },
  { label: 'האם קיבל חיסונים מלאים', value: 'receivedFullVaccination' },
  { label: 'חיסונים שלא קיבל הילד', value: 'nonReceivedVaccinations' },
  { label: 'קופת חולים', value: 'hmo' },
  { label: 'הערות', value: 'remarks' },
  { label: 'תאריך התחלת הנוכחות', value: 'attendanceStartingDate' },
  { label: 'מ- (כתובת)', value: 'from' },
  { label: 'תאריך חתימה (היום)', value: 'signingDate' },
  { label: 'שם כיתה', value: 'className' },
  { label: 'סכום תשלום חודשה', value: 'monthlyPayment' },
  { label: 'אמצעי תשלום', value: 'paymentMethod' },
  { label: 'האם ההורים מאשרים לצילום הילד פנימי', value: 'allowsPhotographingInternal' },
  { label: 'האם ההורים מאשרים לצילום הילד חוץ', value: 'allowsPhotographingExternal' },
  { label: 'שם החותם', value: 'approverName' },
  { label: 'סטטוס קרבות החותם', value: 'approverStatus' },
  { label: 'כתובת החותם', value: 'approverAddress' },
  { label: 'מס. פלפון החותם', value: 'approverPhoneNumber' },
  { label: 'שם רופא', value: 'doctorName' },
  { label: 'גן קודם', value: 'previousKindergarten' },
  { label: 'מקום עבודה של הורה 1', value: 'parentWorkPlace1' },
  { label: 'מקום עבודה של הורה 2', value: 'parentWorkPlace2' },
  { label: 'מספר פלפון בעבודה הורה 1', value: 'parentWorkPhoneNumber1' },
  { label: 'מספר פלפון בעבודה הורה 2', value: 'parentWorkPhoneNumber2' },
  { label: 'מספר סידורי של הילד בין אחיו', value: 'childOrderBetweenSiblings' },
  { label: 'שם איש קשר למצב חירום', value: 'emergencyContactName' },
  { label: 'מס איש קשר למצב חירום', value: 'emergencyContactNumber' },
  { label: 'אחר', value: 'other' },
];